import { css } from "@emotion/css";
import type { PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { ActionButtonType, Pagination } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { useAggregateAPIOperationStatus, useMutation, useQuery } from "@octopusdeploy/octopus-react-client";
import type { BlueprintResource, Repository } from "@octopusdeploy/octopus-server-client";
import type { GetProcessTemplatesBffResponse } from "@octopusdeploy/octopus-server-client/dist/src/repositories/blueprintRepository";
import { links, type QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import type { Dictionary } from "lodash";
import * as React from "react";
import { AddProcessTemplateDialog } from "~/areas/blueprints/AddProcessTemplateDialog";
import DeleteProcessTemplate from "~/areas/blueprints/DeleteProcessTemplate";
import OpenDialogButton from "~/components/Dialog/OpenDialogButton";
import FilterSearchBox from "~/components/FilterSearchBox/index";
import OnboardingPage from "~/components/GettingStarted/OnboardingPage";
import Logo from "~/components/Logo/index";
import ExternalLink from "~/components/Navigation/ExternalLink/index";
import InternalLink from "~/components/Navigation/InternalLink/index";
import type { MenuItem } from "~/components/OverflowMenu/OverflowMenu";
import { OverflowMenu, OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import { PaperLayoutVNext } from "~/components/PaperLayout/PaperLayoutVNext";
import { getValueInStorage } from "~/hooks/localStorageHelpers";
import useLocalStorage from "~/hooks/useLocalStorage";
import { DataTable, DataTableBody, DataTableHeader, DataTableHeaderColumn, DataTableRow, DataTableRowColumn } from "~/primitiveComponents/dataDisplay/DataTable";
interface ProcessTemplatesPageProps {
    loaderData: GetProcessTemplatesBffResponse;
    queryParams: ProcessTemplatesPageQueryParams;
    setQueryParams: QueryParamValuesSetter<ProcessTemplatesPageQueryParams>;
}
interface ProcessTemplatesPageQueryParams {
    searchValue: string;
    page: number | undefined;
    pageSize: number | undefined;
}
const defaultPageSize = 30;
export async function processTemplatesPageLoader(repository: Repository, queryParams: ProcessTemplatesPageQueryParams): Promise<GetProcessTemplatesBffResponse> {
    const userPageSize = getValueInStorage(`Octopus.ProcessTemplatesList.PageSize`);
    const pageSize = userPageSize ? parseInt(userPageSize) : defaultPageSize;
    return await repository.Blueprints.getPaginatedTemplates({ skip: ((queryParams.page ?? 1) - 1) * (queryParams.pageSize ?? pageSize), take: queryParams.pageSize ?? pageSize, partialName: queryParams.searchValue });
}
export function ProcessTemplatesPage({ loaderData, queryParams, setQueryParams }: ProcessTemplatesPageProps) {
    const { isInProgress, errors } = useAggregateAPIOperationStatus();
    const [userDefaultPageSize, setUserDefaultPageSize] = useLocalStorage(`Octopus.ProcessTemplatesList.PageSize`, defaultPageSize);
    const { result: pagingData, refetch: reload } = useQuery((repository) => repository.Blueprints.getPaginatedTemplates({
        skip: ((queryParams.page ?? 1) - 1) * (queryParams.pageSize ?? userDefaultPageSize),
        take: queryParams.pageSize ?? userDefaultPageSize,
        partialName: queryParams.searchValue,
    }), [queryParams, userDefaultPageSize], "Get paginated process templates", {
        initialResult: loaderData,
    });
    const onPageChange = (page: number) => {
        setQueryParams({ ...queryParams, page });
    };
    const onPageSizeChange = (pageSize: number) => {
        setQueryParams({ ...queryParams, pageSize: pageSize, page: 1 });
        setUserDefaultPageSize(pageSize);
    };
    const onSearch = (value: string) => {
        setQueryParams({ ...queryParams, searchValue: value, page: 1 });
    };
    const addProcessTemplateAction: PrimaryPageAction = {
        type: "custom",
        key: "Add Process Template",
        hasPermissions: true,
        content: <AddProcessTemplateButton />,
    };
    const { execute: deleteProcessTemplate } = useMutation({
        name: "Delete Process Template",
        action: (repository: Repository, processTemplate: BlueprintResource) => repository.Blueprints.del({ Id: processTemplate.Id, ChangeDescription: `Delete Process Template '${processTemplate.Name}'` }),
        afterComplete: async () => reload(),
    });
    return (<PaperLayoutVNext title="Templates" busy={isInProgress} errors={errors} primaryAction={addProcessTemplateAction}>
            <>
                {pagingData.TotalNoOfProcessTemplates === 0 ? (<ProcessTemplatesOnboarding />) : (<div>
                        <div className={styles.searchBoxContainer}>
                            <FilterSearchBox value={queryParams.searchValue} onChange={onSearch} placeholder={"Filter by name..."} autoFocus={false} debounceDelay={500}/>
                        </div>
                        <DataTable>
                            <DataTableHeader>
                                <DataTableRow>
                                    <DataTableHeaderColumn>Name</DataTableHeaderColumn>
                                    <DataTableHeaderColumn>Description</DataTableHeaderColumn>
                                </DataTableRow>
                            </DataTableHeader>
                            <DataTableBody>
                                {pagingData.ProcessTemplates.map((pt) => (<ProcessTemplateRow processTemplate={pt} projectToSpaceMap={pagingData.ProjectToSpaceMap} deleteProcessTemplate={deleteProcessTemplate} key={pt.Id}/>))}
                            </DataTableBody>
                        </DataTable>
                        <div className={styles.paginationContainer}>
                            <Pagination label="Process Templates" totalResults={pagingData.TotalResults} itemsPerPageOptions={[30, 50, 100]} selectedItemsPerPage={pagingData.ItemsPerPage} currentPage={queryParams.page ?? 1} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange}/>
                        </div>
                    </div>)}
            </>
        </PaperLayoutVNext>);
}
//TODO put correct link to docs in callout
const ProcessTemplatesOnboarding = () => (<OnboardingPage title="Add your first Process Template to get started!" intro="Process Templates are a group of steps that are reusable accross all projects." learnMore={<ExternalLink href="">Learn more</ExternalLink>}/>);
interface ProcessTemplateRowProps {
    processTemplate: BlueprintResource;
    projectToSpaceMap: Dictionary<string> | undefined;
    deleteProcessTemplate: (processTemplate: BlueprintResource) => Promise<void>;
}
function ProcessTemplateRow({ processTemplate, projectToSpaceMap, deleteProcessTemplate }: ProcessTemplateRowProps) {
    return (<DataTableRow className={styles.middle}>
            <DataTableRowColumn className={styles.middle}>
                <ProcessTemplateNameCell processTemplate={processTemplate} projectToSpaceMap={projectToSpaceMap}/>
            </DataTableRowColumn>
            <DataTableRowColumn className={styles.middle}>
                <div className={styles.rowCellPadding}>{processTemplate.Description}</div>
            </DataTableRowColumn>
            <DataTableRowColumn className={styles.right}>
                <div className={styles.rowCellPadding}>
                    <ProcessTemplateOverflowMenu processTemplate={processTemplate} deleteProcessTemplate={deleteProcessTemplate}/>
                </div>
            </DataTableRowColumn>
        </DataTableRow>);
}
interface ProcessTemplateNameCellProps {
    processTemplate: BlueprintResource;
    projectToSpaceMap: Dictionary<string> | undefined;
}
function ProcessTemplateNameCell({ processTemplate, projectToSpaceMap }: ProcessTemplateNameCellProps) {
    const spaceId = projectToSpaceMap ? projectToSpaceMap[processTemplate.ProjectId] : "";
    const linkTarget = links.editBlueprintPage.generateUrl({ spaceId, blueprintId: processTemplate.Id });
    const logoUrl = getProcessTemplateIconUrl(processTemplate, spaceId);
    return (<div>
            <InternalLink className={styles.nameContainer} to={linkTarget}>
                <div>
                    <Logo url={logoUrl} size={"2.25rem"}/>
                </div>
                <div className={styles.name}>{processTemplate.Name}</div>
            </InternalLink>
        </div>);
}
function getProcessTemplateIconUrl(processTemplate: BlueprintResource, spaceId: string) {
    const processTemplateId = encodeURIComponent(processTemplate.Id);
    return `~/bff/spaces/${spaceId}/blueprints/${processTemplateId}/icon`;
}
interface ProcessTemplateOverflowProps {
    processTemplate: BlueprintResource;
    deleteProcessTemplate: (processTemplate: BlueprintResource) => Promise<void>;
}
function ProcessTemplateOverflowMenu({ processTemplate, deleteProcessTemplate }: ProcessTemplateOverflowProps) {
    const overFlowActions: Array<MenuItem | MenuItem[]> = [];
    overFlowActions.push(OverflowMenuItems.deleteItem("Delete", "Are you sure you want to delete this Process Template?", () => deleteProcessTemplate(processTemplate), <DeleteProcessTemplate processTemplateName={processTemplate.Name}/>));
    return <OverflowMenu menuItems={overFlowActions}/>;
}
function AddProcessTemplateButton() {
    return (<OpenDialogButton disabled={false} label={"Add Process Template"} title={"Create new process template"} type={ActionButtonType.Primary}>
            <AddProcessTemplateDialog />
        </OpenDialogButton>);
}
const styles = {
    rowCellPadding: css({ paddingLeft: space[1] }),
    middle: css({
        verticalAlign: "middle !important",
    }),
    right: css({
        verticalAlign: "middle !important",
        float: "right",
    }),
    nameContainer: css({ display: "flex", alignItems: "center" }),
    name: css({ paddingLeft: space[6] }),
    searchBoxContainer: css({ paddingLeft: space[16] }),
    paginationContainer: css({
        verticalAlign: "middle !important",
        float: "right",
        paddingTop: space[4],
    }),
};
